import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import { useQuery, useMutation } from '@apollo/client';
import { UserMetricPermissions } from '../UserMetricPermissions';
import { User } from 'src/utils/gql/types';
import { GET_COMPANY_MEMBERS } from 'src/utils/gql/queries/userMetricPermission';
import { UPDATE_USER, CREATE_USER, DELETE_USER } from 'src/utils/gql/mutations';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useAccount } from 'src/providers/account/hook/useAccount';
import {
  useIsSuperAdmin,
  useIsCompanyAdmin,
  useCanManageUser,
  useCanPromoteToRole,
  useCurrentUserRole,
} from 'src/hooks/useIsAdmin';

interface TeamMember extends User {
  lastAccess: string;
  status: string;
}

interface TeamTableProps {
  loading?: boolean;
  onAddMember?: () => void;
}

export const TeamTable: React.FC<TeamTableProps> = ({
  loading = false,
  onAddMember,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const snackbar = useSnackbar();

  // Hooks per l'autorizzazione
  const isSuperAdmin = useIsSuperAdmin();
  const isCompanyAdmin = useIsCompanyAdmin();
  const currentUserRole = useCurrentUserRole();

  // Hook to get current user
  const { user: currentUser } = useAccount();

  // Helper functions for permissions
  const canManageUser = (targetUserRole: string) => {
    // Only Admins can view and modify all users
    if (currentUserRole === 'admin') {
      return true;
    }

    // Company Admins can only see User and Company Admin, not Admins
    if (currentUserRole === 'company_admin') {
      return targetUserRole !== 'admin';
    }

    // Users cannot see any other user
    return false;
  };

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',

    email: '',
    role: isSuperAdmin ? 'user' : 'customer',
  });
  const [createFormData, setCreateFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'customer', // Default per tutti, non basato su isSuperAdmin
  });

  // Query per ottenere i membri del team
  const {
    data: membersData,
    loading: loadingMembers,
    refetch,
    error,
  } = useQuery(GET_COMPANY_MEMBERS, {
    onError: (error) => {
      console.error('❌ Errore query GET_COMPANY_MEMBERS:', error);
      console.error('❌ GraphQL Errors:', error.graphQLErrors);
      console.error('❌ Network Error:', error.networkError);
      console.error('❌ Error Message:', error.message);
      snackbar(error.message);
    },
    onCompleted: (data) => {
      // Debug log removed for production
    },
    fetchPolicy: 'network-only', // Forza il refetch ogni volta
  });

  // Debug log removed for production

  // Mutation to update user
  const [updateUser, { loading: updatingUser }] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      snackbar('User updated successfully!', 'success');
      refetch(); // Ricarica i dati
    },
    onError: (error) => {
      snackbar(error.message);
    },
  });

  // Mutation to create user
  const [createUser, { loading: creatingUser }] = useMutation(CREATE_USER, {
    onCompleted: (data) => {
      console.log('✅ User created successfully:', data);
      snackbar('User created successfully!', 'success');
      refetch(); // Ricarica i dati
      setCreateModalOpen(false);
      setCreateFormData({
        firstName: '',
        lastName: '', // AGGIUNTA PROPRIETÀ MANCANTE
        email: '',
        role: 'customer', // Default per tutti
      });
    },
    onError: (error) => {
      console.error('❌ User creation error:', error);
      snackbar(error.message);
    },
  });

  // Mutation to delete user
  const [deleteUser, { loading: deletingUser }] = useMutation(DELETE_USER, {
    onCompleted: () => {
      snackbar('User deleted successfully!', 'success');
      refetch(); // Ricarica i dati
    },
    onError: (error) => {
      snackbar(error.message);
    },
  });

  // Converti i dati del server in TeamMember e filtra in base ai permessi
  const allTeamMembers: TeamMember[] = (
    membersData?.getCompanyMembers || []
  ).map((member: User) => ({
    ...member,
    lastAccess: 'Today', // TODO: implementare logica per ultimo accesso
    status: 'active', // TODO: implementare logica per status
  }));

  // Filter users based on current user permissions
  const filteredTeamMembers: TeamMember[] = allTeamMembers.filter((member) => {
    // If current user is Admin, can see all
    if (isSuperAdmin) {
      return true;
    }

    // If current user is Company Admin, can only see User and Company Admin
    if (isCompanyAdmin) {
      return member.role !== 'admin';
    }

    // Users cannot see any other user
    return false;
  });

  // Sort list putting current user first
  const teamMembers: TeamMember[] = filteredTeamMembers.sort((a, b) => {
    // If a is current user, put it first
    if (a.id === currentUser.id) {
      return -1;
    }
    // If b is current user, put it first
    if (b.id === currentUser.id) {
      return 1;
    }
    // Otherwise maintain original order
    return 0;
  });

  // Debug log removed for production

  const commonFieldSx = {
    maxWidth: {
      xs: '100%',
      sm: '400px',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius * 0.75,
      fontSize: {
        xs: '0.875rem',
        sm: '1rem',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.light',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: {
        xs: '0.875rem',
        sm: '0.9375rem',
      },
      fontWeight: 500,
      '&.Mui-focused': {
        color: 'primary.main',
        fontWeight: 600,
      },
    },
    '& .MuiInputBase-input': {
      padding: {
        xs: theme.spacing(1.5, 2),
        sm: theme.spacing(1.75, 2),
      },
      fontSize: {
        xs: '0.875rem',
        sm: '1rem',
      },
    },
  };

  const handleAddMember = () => {
    setCreateModalOpen(true);
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
    setEditFormData({
      firstName: member.firstName,

      email: member.email,
      role: member.role,
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingMember(null);
    setEditFormData({
      firstName: '',

      email: '',
      role: isSuperAdmin ? 'user' : 'customer',
    });
  };

  const handleSaveEdit = async () => {
    if (editingMember) {
      try {
        await updateUser({
          variables: {
            input: {
              userId: editingMember.id,
              firstName: editFormData.firstName,

              email: editFormData.email,
              role: editFormData.role,
            },
          },
        });
        handleCloseEditModal();
      } catch (error) {
        console.error("Errore durante l'aggiornamento:", error);
      }
    }
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setCreateFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'customer', // Default per tutti
    });
  };

  const handleCreateUser = async () => {
    try {
      await createUser({
        variables: {
          input: {
            firstName: createFormData.firstName,
            email: createFormData.email,
            role: createFormData.role,
          },
        },
      });
    } catch (error) {
      console.error('Errore durante la creazione:', error);
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    try {
      await deleteUser({
        variables: {
          input: {
            userId: memberId,
          },
        },
      });
    } catch (error) {
      console.error("Errore durante l'eliminazione:", error);
    }
  };

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
  };

  const handleBackToTeam = () => {
    setSelectedUser(null);
  };

  if (loading || loadingMembers) {
    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: theme.shape.borderRadius * 1.5,
          padding: theme.spacing(3),
          boxShadow: theme.shadows[1],
          display: 'flex',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (selectedUser) {
    return (
      <UserMetricPermissions user={selectedUser} onBack={handleBackToTeam} />
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: theme.shape.borderRadius * 1.5,
        padding: {
          xs: theme.spacing(2),
          sm: theme.spacing(2.5),
          md: theme.spacing(3),
        },
        boxShadow: theme.shadows[1],
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          gap: {
            xs: 2,
            sm: 0,
          },
        }}
      >
        <Typography variant="h5" component="h1">
          Team
        </Typography>
        {(isSuperAdmin || isCompanyAdmin) && (
          <Tooltip
            title="Add member"
            placement="top"
            arrow
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: 'rgba(0, 0, 0, 0.87)',
                  color: 'white',
                  fontSize: '0.75rem',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  zIndex: 9999,
                },
                '& .MuiTooltip-arrow': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
              },
            }}
          >
            <IconButton
              onClick={handleAddMember}
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
                alignSelf: { xs: 'flex-end', sm: 'auto' },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Tabella per desktop e tablet */}
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            '& .MuiTableCell-root': {
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
              padding: {
                xs: theme.spacing(1, 1),
                sm: theme.spacing(1.5, 1.5),
                md: theme.spacing(2, 1.5),
              },
              textAlign: 'center',
              verticalAlign: 'middle',
              fontSize: {
                xs: '0.875rem',
                sm: '0.9rem',
                md: '1rem',
              },
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: theme.palette.grey[50],
              color: 'text.primary',
              fontWeight: 600,
              fontSize: {
                xs: '0.8rem',
                sm: '0.875rem',
                md: '0.9rem',
              },
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            },
            '& .MuiTableRow-root:nth-of-type(even)': {
              backgroundColor: theme.palette.grey[50],
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Last Access</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Invite</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip
                        title="Manage user permissions"
                        placement="top"
                        arrow
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              backgroundColor: 'rgba(0, 0, 0, 0.87)',
                              color: 'white',
                              fontSize: '0.75rem',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              zIndex: 9999,
                            },
                            '& .MuiTooltip-arrow': {
                              color: 'rgba(0, 0, 0, 0.87)',
                            },
                          },
                        }}
                      >
                        <Chip
                          label={`${member.firstName} ${member.lastName}`}
                          size="medium"
                          onClick={() => handleUserClick(member)}
                          icon={
                            <SecurityIcon
                              sx={{ fontSize: '1rem', color: 'inherit' }}
                            />
                          }
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            fontWeight: 500,
                            borderRadius: '6px',
                            padding: theme.spacing(1, 2),
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                              transform: 'translateY(-1px)',
                            },
                            '& .MuiChip-icon': {
                              marginLeft: theme.spacing(0.5),
                            },
                          }}
                        />
                      </Tooltip>
                      {member.id === currentUser.id && (
                        <Chip
                          label="You"
                          size="small"
                          sx={{
                            backgroundColor: 'warning.main',
                            color: 'warning.contrastText',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '20px',
                            '& .MuiChip-label': {
                              padding: '0 6px',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {member.lastAccess}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        member.role === 'admin'
                          ? 'Admin'
                          : member.role === 'company_admin'
                            ? 'Company Admin'
                            : 'User'
                      }
                      size="small"
                      sx={{
                        backgroundColor:
                          member.role === 'admin'
                            ? theme.palette.warning.light
                            : member.role === 'company_admin'
                              ? theme.palette.info.light
                              : theme.palette.success.light,
                        color: 'text.primary',
                        fontWeight: 500,
                        borderRadius: '4px',
                        padding: theme.spacing(0.5, 1.5),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Accepted
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {/* Show edit button only if user can manage this member */}
                      {canManageUser(member.role) && (
                        <Tooltip
                          title="Edit"
                          placement="top"
                          arrow
                          PopperProps={{
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                backgroundColor: 'rgba(0, 0, 0, 0.87)',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                zIndex: 9999,
                              },
                              '& .MuiTooltip-arrow': {
                                color: 'rgba(0, 0, 0, 0.87)',
                              },
                            },
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEditMember(member)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: 'primary.light',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Show delete button only if user can manage this member */}
                      {canManageUser(member.role) && (
                        <Tooltip
                          title="Delete"
                          placement="top"
                          arrow
                          PopperProps={{
                            sx: {
                              '& .MuiTooltip-tooltip': {
                                backgroundColor: 'rgba(0, 0, 0, 0.87)',
                                color: 'white',
                                fontSize: '0.75rem',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                zIndex: 9999,
                              },
                              '& .MuiTooltip-arrow': {
                                color: 'rgba(0, 0, 0, 0.87)',
                              },
                            },
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMember(member.id)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                                backgroundColor: 'error.light',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Cards per mobile */}
      <Stack spacing={2} sx={{ display: { xs: 'block', sm: 'none' } }}>
        {teamMembers.map((member) => (
          <Card
            key={member.id}
            onClick={() => handleUserClick(member)}
            sx={{
              borderRadius: '8px',
              boxShadow: theme.shadows[1],
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: theme.shadows[2],
                transform: 'translateY(-1px)',
              },
            }}
          >
            <CardContent
              sx={{
                padding: theme.spacing(2),
                '&:last-child': {
                  paddingBottom: theme.spacing(2),
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Tooltip
                    title="Manage user permissions"
                    placement="top"
                    arrow
                    PopperProps={{
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: 'rgba(0, 0, 0, 0.87)',
                          color: 'white',
                          fontSize: '0.75rem',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          zIndex: 9999,
                        },
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      },
                    }}
                  >
                    <Chip
                      label={`${member.firstName} ${member.lastName}`}
                      size="medium"
                      icon={
                        <SecurityIcon
                          sx={{ fontSize: '1rem', color: 'inherit' }}
                        />
                      }
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 500,
                        borderRadius: '6px',
                        padding: theme.spacing(1, 2),
                        '& .MuiChip-icon': {
                          marginLeft: theme.spacing(0.5),
                        },
                      }}
                    />
                  </Tooltip>
                  {member.id === currentUser.id && (
                    <Chip
                      label="You"
                      size="small"
                      sx={{
                        backgroundColor: 'warning.main',
                        color: 'warning.contrastText',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '20px',
                        '& .MuiChip-label': {
                          padding: '0 6px',
                        },
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Chip
                    label={
                      member.role === 'admin'
                        ? 'Admin'
                        : member.role === 'company_admin'
                          ? 'Company Admin'
                          : 'User'
                    }
                    size="small"
                    sx={{
                      backgroundColor:
                        member.role === 'admin'
                          ? theme.palette.warning.light
                          : member.role === 'company_admin'
                            ? theme.palette.info.light
                            : theme.palette.success.light,
                      color: 'text.primary',
                      fontWeight: 500,
                      borderRadius: '4px',
                      padding: theme.spacing(0.5, 1.5),
                      whiteSpace: 'nowrap',
                      minWidth: 'fit-content',
                    }}
                  />
                </Box>
              </Box>

              <Stack spacing={1.5}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Last Access:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontWeight: 500 }}
                  >
                    {member.lastAccess}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Invite Status:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ fontWeight: 500 }}
                  >
                    Accepted
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: `1px solid ${theme.palette.grey[200]}`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Show edit button only if user can manage this member */}
                {canManageUser(member.role) && (
                  <Tooltip
                    title="Edit"
                    placement="top"
                    arrow
                    PopperProps={{
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: 'rgba(0, 0, 0, 0.87)',
                          color: 'white',
                          fontSize: '0.75rem',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          zIndex: 9999,
                        },
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleEditMember(member)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'primary.light',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {/* Show delete button only if user can manage this member */}
                {canManageUser(member.role) && (
                  <Tooltip
                    title="Delete"
                    placement="top"
                    arrow
                    PopperProps={{
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: 'rgba(0, 0, 0, 0.87)',
                          color: 'white',
                          fontSize: '0.75rem',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          zIndex: 9999,
                        },
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMember(member.id)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: 'error.light',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Modale di modifica */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.shape.borderRadius * 2,
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
            minWidth: { xs: '90vw', sm: '450px' },
            maxWidth: { xs: '95vw', sm: '500px' },
            margin: { xs: theme.spacing(2), sm: 'auto' },
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 700,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            padding: {
              xs: theme.spacing(3, 3, 2.5),
              sm: theme.spacing(3.5, 4, 3),
            },
            borderBottom: `1px solid ${theme.palette.primary.light}`,
            textAlign: 'center',
          }}
        >
          Modify Team Member
        </DialogTitle>

        <DialogContent
          sx={{
            marginTop: theme.spacing(4),
            px: theme.spacing(4),
            pb: theme.spacing(4),
            backgroundColor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(3),
          }}
        >
          {/* Campo Nome */}
          <TextField
            fullWidth
            label="Name"
            value={editFormData.firstName}
            onChange={(e) =>
              setEditFormData({ ...editFormData, firstName: e.target.value })
            }
            size={isMobile ? 'small' : 'medium'}
            sx={commonFieldSx}
          />

          {/* Campo Email */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={editFormData.email}
            onChange={(e) =>
              setEditFormData({ ...editFormData, email: e.target.value })
            }
            size={isMobile ? 'small' : 'medium'}
            sx={commonFieldSx}
          />

          {/* Campo Livello Permessi */}
          <FormControl
            fullWidth
            sx={{
              ...commonFieldSx,
              '& .MuiOutlinedInput-root': {
                padding: 0,
                borderRadius: theme.shape.borderRadius * 0.75,
              },
              '& .MuiInputLabel-root': {
                marginBottom: theme.spacing(3),
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.secondary',
              },
            }}
          >
            <InputLabel id="role-label" sx={{ mb: 5 }}>
              Permission Level
            </InputLabel>

            <Select
              labelId="role-label"
              id="role"
              value={editFormData.role}
              onChange={(e) =>
                setEditFormData({ ...editFormData, role: e.target.value })
              }
              size={isMobile ? 'small' : 'medium'}
              displayEmpty
              sx={{
                borderRadius: theme.shape.borderRadius * 0.75,
                '& .MuiSelect-select': {
                  padding: {
                    xs: theme.spacing(2, 2),
                    sm: theme.spacing(2.5, 2),
                  },
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                  },
                  paddingRight: {
                    xs: theme.spacing(5),
                    sm: theme.spacing(6),
                  },
                },
                '& .MuiSelect-icon': {
                  right: theme.spacing(1.5),
                },
              }}
            >
              <MenuItem value="customer">User</MenuItem>
              {/* Solo gli Admin possono promuovere a Company Admin */}
              {isSuperAdmin && (
                <MenuItem value="company_admin">Company Admin</MenuItem>
              )}
              {/* Solo gli Admin possono promuovere ad Admin */}
              {isSuperAdmin && <MenuItem value="admin">Admin</MenuItem>}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: theme.spacing(3), sm: theme.spacing(4) },
            py: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.grey[200]}`,
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-end' },
          }}
        >
          <Button
            onClick={handleCloseEditModal}
            variant="outlined"
            sx={{
              borderRadius: theme.shape.borderRadius * 0.75,
              px: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
              py: theme.spacing(1.25),
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { xs: '100%', sm: '100px' },
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={updatingUser}
            sx={{
              borderRadius: theme.shape.borderRadius * 0.75,
              px: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
              py: theme.spacing(1.25),
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { xs: '100%', sm: '100px' },
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4],
              },
            }}
          >
            {updatingUser ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale di creazione */}
      <Dialog
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.shape.borderRadius * 2,
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
            minWidth: { xs: '90vw', sm: '450px' },
            maxWidth: { xs: '95vw', sm: '500px' },
            margin: { xs: theme.spacing(2), sm: 'auto' },
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 700,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            padding: {
              xs: theme.spacing(3, 3, 2.5),
              sm: theme.spacing(3.5, 4, 3),
            },
            borderBottom: `1px solid ${theme.palette.primary.light}`,
            textAlign: 'center',
          }}
        >
          Create New User
        </DialogTitle>

        <DialogContent
          sx={{
            marginTop: theme.spacing(4),
            px: theme.spacing(4),
            pb: theme.spacing(4),
            backgroundColor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(3),
          }}
        >
          {/* Campo Nome */}
          <TextField
            fullWidth
            label="Name"
            value={createFormData.firstName}
            onChange={(e) =>
              setCreateFormData({
                ...createFormData,
                firstName: e.target.value,
              })
            }
            size={isMobile ? 'small' : 'medium'}
            sx={commonFieldSx}
          />

          {/* Campo Email */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={createFormData.email}
            onChange={(e) =>
              setCreateFormData({ ...createFormData, email: e.target.value })
            }
            size={isMobile ? 'small' : 'medium'}
            sx={commonFieldSx}
          />

          {/* Campo Livello Permessi */}
          <FormControl
            fullWidth
            sx={{
              ...commonFieldSx,
              '& .MuiOutlinedInput-root': {
                padding: 0,
                borderRadius: theme.shape.borderRadius * 0.75,
              },
              '& .MuiInputLabel-root': {
                marginBottom: theme.spacing(3),
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.secondary',
              },
            }}
          >
            <InputLabel id="role-label" sx={{ mb: 5 }}>
              Permission Level
            </InputLabel>

            <Select
              labelId="role-label"
              id="role"
              value={createFormData.role}
              onChange={(e) =>
                setCreateFormData({ ...createFormData, role: e.target.value })
              }
              size={isMobile ? 'small' : 'medium'}
              displayEmpty
              sx={{
                borderRadius: theme.shape.borderRadius * 0.75,
                '& .MuiSelect-select': {
                  padding: {
                    xs: theme.spacing(2, 2),
                    sm: theme.spacing(2.5, 2),
                  },
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                  },
                  paddingRight: {
                    xs: theme.spacing(5),
                    sm: theme.spacing(6),
                  },
                },
                '& .MuiSelect-icon': {
                  right: theme.spacing(1.5),
                },
              }}
            >
              <MenuItem value="customer">User</MenuItem>
              {/* Company Admin può creare altri Company Admin */}
              {(isSuperAdmin || isCompanyAdmin) && (
                <MenuItem value="company_admin">Company Admin</MenuItem>
              )}
              {/* Solo Admin può creare Admin */}
              {isSuperAdmin && <MenuItem value="admin">Admin</MenuItem>}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: theme.spacing(3), sm: theme.spacing(4) },
            py: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.grey[200]}`,
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-end' },
          }}
        >
          <Button
            onClick={handleCloseCreateModal}
            variant="outlined"
            sx={{
              borderRadius: theme.shape.borderRadius * 0.75,
              px: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
              py: theme.spacing(1.25),
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { xs: '100%', sm: '100px' },
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={creatingUser}
            sx={{
              borderRadius: theme.shape.borderRadius * 0.75,
              px: { xs: theme.spacing(2.5), sm: theme.spacing(3) },
              py: theme.spacing(1.25),
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { xs: '100%', sm: '100px' },
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4],
              },
            }}
          >
            {creatingUser ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Create User'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    // test
  );
};
